import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Descriptions, Button, Tag, Row, Col, Statistic, Space, Divider } from 'antd';
import { EditOutlined, ArrowRightOutlined, ApartmentOutlined, TeamOutlined, IdcardOutlined, ReloadOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { designationsApi } from '../../../utils/api';
import { DetailPageSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

const ViewPosition = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { canEdit } = useResourcePermissions('employees');
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/positions', { state: buildGoBackState() });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await designationsApi.getById(id);
      setItem(res.data);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت اطلاعات سمت');
      goBackToList();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6">
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (!item) return null;

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center shadow-lg">
                <ApartmentOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">{item.name}</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-sm m-0">جزئیات سمت شغلی و والد (در صورت وجود)</p> */}
              </div>
            </div>
            <Space size="middle">
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ fontFamily: 'CustomFont' }}>
                بروزرسانی
              </Button>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
              {canEdit ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`/hr/positions/${id}/edit`, {
                      state: { returnTo: returnToRef.current, ...buildGoBackState() },
                    })
                  }
                  className="bg-blue-600 hover:bg-blue-700 border-0"
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش
                </Button>
              ) : null}
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <Card
                  className="shadow-sm border border-gray-100 dark:border-gray-700 mb-4 bg-white dark:bg-gray-800"
                  title={
                    <div className="flex items-center gap-2">
                      <IdcardOutlined className="text-blue-500" />
                      <span style={{ fontFamily: 'CustomFont', fontSize: '18px', fontWeight: 600 }}>
                        اطلاعات اصلی
                      </span>
                    </div>
                  }
                >
                  <Descriptions
                    column={1}
                    bordered
                    size="middle"
                    style={{ fontFamily: 'CustomFont' }}
                    labelStyle={{ fontWeight: 600, width: '200px', backgroundColor: '#fafafa' }}
                    contentStyle={{ backgroundColor: '#fff' }}
                  >
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <IdcardOutlined /> شناسه
                        </span>
                      }
                    >
                      <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 10px' }}>
                        #{item.id}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ApartmentOutlined /> نام سمت
                        </span>
                      }
                    >
                      <span style={{ fontFamily: 'CustomFont', fontSize: '16px', fontWeight: 500 }}>{item.name}</span>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TeamOutlined /> سمت والد
                        </span>
                      }
                    >
                      {item.parent ? (
                        <Tag
                          color="blue"
                          icon={<TeamOutlined />}
                          style={{
                            fontFamily: 'CustomFont',
                            fontSize: '14px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                          onClick={() => navigate(`/hr/positions/${item.parent.id}`)}
                          className="hover:shadow-md transition-all"
                        >
                          {item.parent.name}
                        </Tag>
                      ) : (
                        <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 10px' }}>
                          بدون والد
                        </Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  className="shadow-sm border border-gray-100 dark:border-gray-700 mb-4 bg-white dark:bg-gray-800"
                  title={
                    <div className="flex items-center gap-2">
                      <TeamOutlined className="text-purple-500" />
                      <span style={{ fontFamily: 'CustomFont', fontSize: '18px', fontWeight: 600 }}>
                        خلاصه اطلاعات
                      </span>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                      <Statistic
                        title={<span style={{ fontFamily: 'CustomFont', color: '#666', fontSize: '14px' }}>شناسه</span>}
                        value={item.id}
                        valueStyle={{ color: '#1890ff', fontFamily: 'CustomFont', fontSize: '24px', fontWeight: 'bold' }}
                        prefix={<IdcardOutlined />}
                      />
                    </div>
                    <Divider style={{ margin: '16px 0' }} />
                    <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                      <div className="flex items-center justify-between">
                        <span style={{ fontFamily: 'CustomFont', color: '#666', fontSize: '14px' }}>وضعیت</span>
                        <Tag color="success" style={{ fontFamily: 'CustomFont' }}>
                          فعال
                        </Tag>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-2" style={{ fontFamily: 'CustomFont' }}>
                        این سمت فعال است.
                      </p>
                    </div>
                    {item.parent && (
                      <>
                        <Divider style={{ margin: '16px 0' }} />
                        <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                          <div className="flex items-center justify-between">
                            <span style={{ fontFamily: 'CustomFont', color: '#666', fontSize: '14px' }}>نوع</span>
                            <Tag color="purple" style={{ fontFamily: 'CustomFont' }}>
                              زیرمجموعه
                            </Tag>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewPosition;
